import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, X, Settings, Check } from 'lucide-react'
import { FILL_GRADIENT } from '../../constants/gradients'
import { analyticsService } from '../../services/analyticsService'

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
}

const COOKIE_CONSENT_KEY = 'beemi_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'beemi_cookie_preferences';

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always required
    analytics: false,
    functional: false
  })

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000)
    } else {
      // Load saved preferences and apply to analytics
      const saved = localStorage.getItem(COOKIE_PREFERENCES_KEY)
      if (saved) {
        const prefs = JSON.parse(saved);
        setPreferences(prefs)
        // Set analytics consent based on saved preferences
        analyticsService.setConsent(prefs.analytics)
      } else {
        // If consent was given but no preferences saved, assume accepted
        analyticsService.setConsent(true)
      }
    }
  }, [])

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs))
    setPreferences(prefs)
    
    // Set analytics consent
    analyticsService.setConsent(prefs.analytics)
    
    // Apply cookie preferences
    applyCookiePreferences(prefs)
    
    setShowBanner(false)
    setShowSettings(false)
  }

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      functional: true
    })
  }

  const handleRejectNonEssential = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      functional: false
    })
  }

  const handleSaveCustom = () => {
    savePreferences(preferences)
  }

  const applyCookiePreferences = (prefs: CookiePreferences) => {
    // Disable analytics if not consented
    if (!prefs.analytics) {
      // Disable Firebase Analytics
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        })
      }
      
      // Disable Amplitude
      if (window.amplitude) {
        window.amplitude.setOptOut(true)
      }
    }
    
    console.log('🍪 Cookie preferences applied:', prefs)
  }

  if (!showBanner) return null

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4"
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden">
              {/* Settings Panel */}
              <AnimatePresence>
                {showSettings && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-b border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="p-5 space-y-3">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white">
                          Cookie Preferences
                        </h3>
                        <button
                          onClick={() => setShowSettings(false)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        </button>
                      </div>

                      {/* Necessary Cookies */}
                      <div className="flex items-start gap-2.5 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <input
                          type="checkbox"
                          checked={true}
                          disabled={true}
                          className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-gray-400 opacity-60 cursor-not-allowed"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            Necessary <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">(Required)</span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            Essential for authentication and security.
                          </div>
                        </div>
                      </div>

                      {/* Analytics Cookies */}
                      <div className="flex items-start gap-2.5 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                        <input
                          type="checkbox"
                          checked={preferences.analytics}
                          onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                          className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-pink-600 focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 transition-all"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            Analytics
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            Help us improve the platform.
                          </div>
                        </div>
                      </div>

                      {/* Functional Cookies */}
                      <div className="flex items-start gap-2.5 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                        <input
                          type="checkbox"
                          checked={preferences.functional}
                          onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                          className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-pink-600 focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 transition-all"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            Functional
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            Remember your preferences.
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={handleSaveCustom}
                        className="w-full mt-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all hover:shadow-lg hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                        style={{ background: FILL_GRADIENT }}
                      >
                        <Check className="w-4 h-4" />
                        Save Preferences
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Main Banner */}
              <div className="p-5">
                <div className="flex items-center gap-4">
                  <div 
                    className="p-2.5 rounded-lg flex-shrink-0"
                    style={{ background: FILL_GRADIENT }}
                  >
                    <Cookie className="w-5 h-5 text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                      We use cookies
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      We use cookies for functionality and analytics. See our{' '}
                      <a 
                        href="https://legal.beemi.app/cookie-policy.html" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-pink-600 dark:text-pink-400 hover:underline"
                      >
                        Cookie Policy
                      </a>.
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all hover:shadow-lg hover:scale-105 active:scale-95"
                      style={{ background: FILL_GRADIENT }}
                    >
                      Accept All
                    </button>
                    <button
                      onClick={handleRejectNonEssential}
                      className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => setShowSettings(!showSettings)}
                      className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Customize
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default CookieConsentBanner
